import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { gsap } from '@shared/lib/gsap'
import { getPostMeta, getRelatedPosts, getPostAlternates, loadPostHtml, categorySlug, type PostMeta } from '@shared/lib/posts'
import { localeCategoryRoute, localeRoot, postLocaleFromPath, POST_LOCALE_LABELS } from '@shared/lib/post-locale'
import { useSeo, withSiteName } from '@shared/lib/seo'
import { SITE } from '@shared/config'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import { EditorByline } from '@shared/ui/EditorByline'
import * as styles from './PostPage.css'

function formatDate(dateStr: string, locale: 'en' | 'fr' | 'es'): string {
  const calendarDate = dateStr.match(/^(\d{4}-\d{2}-\d{2})/)?.[1]
  const date = new Date(calendarDate ? `${calendarDate}T00:00:00Z` : dateStr)
  return date.toLocaleDateString(POST_LOCALE_LABELS[locale].htmlLang, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

function estimateReadingMinutes(html: string): number {
  const words = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean).length
  return Math.max(1, Math.round(words / 220))
}

type TocItem = { id: string; text: string; level: number }
type TocNode = { id: string; text: string; children: { id: string; text: string }[] }

function extractToc(html: string): TocItem[] {
  if (!html || typeof DOMParser === 'undefined') return []
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.querySelectorAll('h2[id], h3[id]')).map((h) => ({
    id: (h as HTMLElement).id,
    text: (h.textContent || '').trim(),
    level: Number(h.tagName[1]),
  }))
}

export function PostPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const locale = postLocaleFromPath(location.pathname)
  const labels = POST_LOCALE_LABELS[locale]
  const proseRef = useRef<HTMLDivElement>(null)

  const meta = slug ? getPostMeta(slug, locale) : undefined
  const wasUpdated = Boolean(meta && meta.modified.slice(0, 10) !== meta.date.slice(0, 10))
  const [html, setHtml] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [activeId, setActiveId] = useState('')

  useEffect(() => {
    if (!slug) return
    let live = true
    if (!meta) return () => { live = false }
    loadPostHtml(meta).then((r) => { if (live) setHtml(r ? r.html : '') })
    return () => { live = false }
  }, [slug, meta])

  const toc = useMemo(() => (html ? extractToc(html) : []), [html])
  const tocTree = useMemo<TocNode[]>(() => {
    const tree: TocNode[] = []
    for (const it of toc) {
      if (it.level === 2 || tree.length === 0) tree.push({ id: it.id, text: it.text, children: [] })
      else tree[tree.length - 1].children.push({ id: it.id, text: it.text })
    }
    return tree
  }, [toc])

  // Mirror the prerender exactly: no hreflang until a translated sibling
  // exists, then the full set plus x-default pointing at the English page.
  const seoAlternates = useMemo(() => {
    if (!meta) return []
    const siblings = getPostAlternates(meta)
    if (siblings.length < 2) return []
    const english = siblings.find((candidate) => candidate.locale === 'en')
    return [
      ...siblings.map((candidate) => ({
        lang: POST_LOCALE_LABELS[candidate.locale].htmlLang,
        path: candidate.route,
      })),
      ...(english ? [{ lang: 'x-default', path: english.route }] : []),
    ]
  }, [meta])

  useSeo({
    // English titles are always branded (matching the static build); locale
    // titles keep the brand only when the result still fits a SERP title.
    title: meta ? (meta.locale === 'en' ? `${meta.title} | ${SITE.name}` : withSiteName(meta.title)) : SITE.name,
    description: meta?.description,
    path: meta?.route ?? `/${slug ?? ''}`,
    image: meta?.thumbnail,
    type: 'article',
    lang: labels.htmlLang,
    alternates: seoAlternates,
  })

  useEffect(() => { window.scrollTo({ top: 0 }) }, [slug])

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement
      const max = el.scrollHeight - el.clientHeight
      setProgress(max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 0)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [html])

  useEffect(() => {
    if (!proseRef.current || toc.length === 0) return
    const heads = toc
      .map((t) => proseRef.current!.querySelector<HTMLElement>(`[id="${t.id}"]`))
      .filter(Boolean) as HTMLElement[]
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (vis[0]) setActiveId((vis[0].target as HTMLElement).id)
      },
      { rootMargin: '-90px 0px -70% 0px', threshold: 0 },
    )
    heads.forEach((h) => obs.observe(h))
    return () => obs.disconnect()
  }, [toc])

  useEffect(() => {
    if (!proseRef.current || html === null) return
    const ctx = gsap.context(() => {
      gsap.from(proseRef.current, { opacity: 0, y: 16, duration: 0.6, ease: 'power3.out', delay: 0.05 })
    })
    return () => ctx.revert()
  }, [html])

  if (!meta) {
    return (
      <div className={styles.page}>
        <Header />
        <div style={{ paddingTop: '140px', paddingLeft: '4rem', color: 'var(--color-textMuted)' }}>{labels.postNotFound}</div>
        <Footer />
      </div>
    )
  }

  const related = getRelatedPosts(meta)
  const goTo = (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className={styles.page}>
      <div className="post-progress" style={{ width: `${progress}%` }} />
      <Header />

      <div className={styles.hero}>
        <nav className="post-breadcrumb" aria-label="Breadcrumb">
          <Link to={localeRoot(locale)}>{labels.home}</Link>
          {meta.category && (
            <>
              <span>›</span>
              <Link to={localeCategoryRoute(locale, categorySlug(meta.category))}>
                {meta.category}
              </Link>
            </>
          )}
        </nav>

        <div className={styles.heroMeta}>
          <span className={styles.categoryBadge}>{meta.category}</span>
          <span className={styles.heroDate}>
            {wasUpdated ? `${labels.updated} ${formatDate(meta.modified, locale)}` : formatDate(meta.date, locale)}
          </span>
        </div>

        <h1 className={styles.heroTitle}>{meta.title}</h1>
        <p className={styles.heroDesc}>{meta.description}</p>

        <div className={styles.heroAuthor}>
          <EditorByline name={meta.author} />
          {meta.reviewedBy && (
            <>
              <span className={styles.authorDot} />
              <span>{labels.reviewedBy} {meta.reviewedBy}</span>
            </>
          )}
          <span className={styles.authorDot} />
          <span>{formatDate(meta.date, locale)}</span>
          {html && (
            <>
              <span className={styles.authorDot} />
              <span>{estimateReadingMinutes(html)} {labels.minRead}</span>
            </>
          )}
        </div>
      </div>

      {meta.thumbnail && (
        <img src={meta.thumbnail} alt={meta.title} className={styles.thumbnail} fetchPriority="high" decoding="async" />
      )}

      <div className={styles.layout}>
        {html === null ? (
          <div className={`${styles.prose} post-content`} style={{ opacity: 0.5 }}>Loading…</div>
        ) : (
          <div ref={proseRef} className={`${styles.prose} post-content`} dangerouslySetInnerHTML={{ __html: html }} />
        )}

        <aside className={styles.sidebar}>
          <div className="sidebar-meta">
            <div className={styles.sidebarSection}>
              <p className={styles.sidebarTitle}>{labels.postInfo}</p>
              <div className={styles.sidebarInfo}>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>{labels.author}</span>
                  <span className={styles.sidebarInfoValue}>{meta.author}</span>
                </div>
                {meta.reviewedBy && (
                  <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>{labels.reviewedBy}</span>
                    <span className={styles.sidebarInfoValue}>{meta.reviewedBy}</span>
                  </div>
                )}
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>{labels.date}</span>
                  <span className={styles.sidebarInfoValue}>{formatDate(meta.date, locale)}</span>
                </div>
                {wasUpdated && (
                  <div className={styles.sidebarInfoRow}>
                    <span className={styles.sidebarInfoLabel}>{labels.updated}</span>
                    <span className={styles.sidebarInfoValue}>{formatDate(meta.modified, locale)}</span>
                  </div>
                )}
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>{labels.category}</span>
                  <span className={styles.sidebarInfoValue}>{meta.category}</span>
                </div>
              </div>
            </div>

            {meta.tags.length > 0 && (
              <div className={styles.sidebarSection}>
                <p className={styles.sidebarTitle}>{labels.tags}</p>
                <div className={styles.tagList}>
                  {meta.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {tocTree.length > 0 && (
            <nav className="post-toc" aria-label="Table of contents">
              <p className="post-toc__label">{labels.onThisPage}</p>
              <ul className="toc-tree">
                {tocTree.map((node) => (
                  <li key={node.id} className="toc-node">
                    <a href={`#${node.id}`} className={activeId === node.id ? 'active' : ''} onClick={(e) => goTo(e, node.id)}>
                      {node.text}
                    </a>
                    {node.children.length > 0 && (
                      <ul className="toc-subtree">
                        {node.children.map((c) => (
                          <li key={c.id}>
                            <a href={`#${c.id}`} className={activeId === c.id ? 'active' : ''} onClick={(e) => goTo(e, c.id)}>
                              {c.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </aside>
      </div>

      {related.length > 0 && (
        <div className={styles.relatedSection}>
          <h2 className={styles.relatedTitle}>{labels.relatedPosts}</h2>
          <div className={styles.relatedGrid}>
            {related.map((p) => (
              <RelatedCard key={p.slug} post={p} />
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}

function RelatedCard({ post }: { post: PostMeta }) {
  return (
    <Link className={styles.relatedCard} to={post.route} data-analytics="related-post">
      <div className={styles.relatedCardImgWrap}>
        <img src={post.thumbnail} alt={post.title} className={styles.relatedCardImg} loading="lazy" />
      </div>
      <div className={styles.relatedCardBody}>
        <p className={styles.relatedCardCategory}>{post.category}</p>
        <h3 className={styles.relatedCardTitle}>{post.title}</h3>
      </div>
    </Link>
  )
}
